import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneMockup } from './PhoneMockup';
import { ScreenChatInbox } from './ScreenChatInbox';
import { ScreenKanban } from './ScreenKanban';
import { ScreenProvider } from './ScreenProvider';
import { ScreenDashboard } from './ScreenDashboard';

const ChatIcon = ({ size = 18 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const ClipboardIcon = ({ size = 18 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="M9 12h6" />
    <path d="M9 16h6" />
  </svg>
);

const PhoneIcon = ({ size = 18 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
    <path d="M12 18h.01" />
  </svg>
);

const ChartIcon = ({ size = 18 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18" />
    <path d="M7 16V10" />
    <path d="M12 16V6" />
    <path d="M17 16v-4" />
  </svg>
);

interface TabConfig {
  keyBase: string;
  icon: React.ReactNode;
  screen: React.ReactNode;
}

const TAB_CONFIGS: TabConfig[] = [
  { keyBase: 'landing.features.tabs.crm', icon: <ChatIcon />, screen: <ScreenChatInbox /> },
  { keyBase: 'landing.features.tabs.operations', icon: <ClipboardIcon />, screen: <ScreenKanban /> },
  { keyBase: 'landing.features.tabs.providerApp', icon: <PhoneIcon />, screen: <ScreenProvider /> },
  { keyBase: 'landing.features.tabs.ownerView', icon: <ChartIcon />, screen: <ScreenDashboard /> },
];

export const FeaturesSection = React.memo(() => {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % TAB_CONFIGS.length), 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="funcionalidades" className="py-24 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">{t('landing.features.eyebrow')}</span>
          <h2
            className="text-3xl md:text-5xl font-medium mt-3 mb-5"
            style={{
              background: 'linear-gradient(to bottom, #000000, #000000, rgba(0, 0, 0, 0.6))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.04em',
            }}
          >
            {t('landing.features.titleLine1')}
            <br />
            {t('landing.features.titleLine2')}
          </h2>
          <p className="text-base max-w-2xl mx-auto text-gray-500">{t('landing.features.subtitle')}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="flex flex-col gap-3">
            {TAB_CONFIGS.map((cfg, i) => {
              const isActive = i === active;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActive(i)}
                  className={`text-left rounded-2xl p-5 border transition-all duration-300 ${isActive ? 'bg-white border-black shadow-lg shadow-gray-200' : 'bg-white/40 border-transparent hover:border-gray-200'}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-colors ${isActive ? 'bg-black text-white' : 'bg-gray-100 text-gray-500'}`}>
                      {cfg.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-[11px] font-semibold uppercase tracking-widest ${isActive ? 'text-black' : 'text-gray-400'}`}>{t(`${cfg.keyBase}.eyebrow`)}</span>
                      <h3 className={`text-base md:text-lg font-semibold mt-1 transition-colors ${isActive ? 'text-black' : 'text-gray-700'}`}>{t(`${cfg.keyBase}.title`)}</h3>
                      {isActive && (
                        <p className="text-sm mt-1.5 leading-relaxed text-gray-500">{t(`${cfg.keyBase}.description`)}</p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex justify-center">
            <PhoneMockup gradient="from-gray-100 via-gray-50 to-white">
              <div key={active}>{TAB_CONFIGS[active].screen}</div>
            </PhoneMockup>
          </div>
        </div>
      </div>
    </section>
  );
});
FeaturesSection.displayName = 'FeaturesSection';
